import { useCallback, useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Bar, BarChart, CartesianGrid, LabelList, Line, LineChart, XAxis, YAxis } from 'recharts';
import { bucketByWeek, sharePercents, type TeamTimeSummary } from '@hygge/shared';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DatePicker } from '@/components/ui/date-picker';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import { Donut } from '@/components/charts/donut';
import { isInactiveProject, withArchivedHidden } from '@/lib/archived-projects';
import { ShowArchivedToggle } from '@/components/show-archived-toggle';
import { ProjectFilterLabel } from '@/components/project-filter-label';
import {
  useRefreshTimePeriods,
  useTeamTimeSummary,
  useTimePeriodPresets,
} from '@/hooks/use-time-entries';
import { formatDate } from '@/lib/date';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { extractErrorMessage } from '@/lib/errors';
import { cn } from '@/lib/utils';
import { formatHours } from '../lib/report-print';
import { useReportProjects } from '../lib/time-tracking-projects';
import {
  customRange,
  isWeekendDayKey,
  memberLabel,
  presetRange,
  projectLabel,
  summaryWindow,
  topWithRest,
  type PeriodPreset,
} from '../lib/dashboard-data';

/**
 * Where the team's time went this period — by project, by person, over time.
 *
 * Three questions, one period, one optional project filter. Deliberately not an
 * analytics product: everything on screen answers a question a lead actually
 * asks on a Monday, and anything that would need a query builder is the Reports
 * tab's job.
 */

/** People shown before the list asks to be expanded. */
const MEMBER_LIMIT = 10;

/**
 * The avatar-and-name column beside the person bars.
 *
 * The width is spent deliberately: a 20px avatar, an 8px gap, a ~116px name and
 * an 8px gutter so no name ever touches a bar. That fits a full "Firstname
 * Lastname" for all but the longest names, which ellipsise and keep the whole
 * thing in a `title`. The previous 176 was cut to the old 26-character clamp,
 * which most names never reached — the 24px it wasted now belongs to the bars.
 * The height is the tick's HTML box: tall enough for the avatar, well inside the
 * 34px a row gets, so neighbouring ticks cannot overlap.
 */
export const MEMBER_TICK_WIDTH = 152;
const MEMBER_TICK_HEIGHT = 24;

/**
 * The person axis, everything except the tick itself. Exported so the
 * regression test renders the REAL configuration instead of a copy that can
 * drift away from it.
 *
 * tickSize/tickMargin are zeroed because MemberTick lays itself out from the
 * axis's right edge. Recharts otherwise insets the tick x by tickSize +
 * tickMargin (6 + 2 by default — `tickLine={false}` does NOT zero them), which
 * emitted the box at x="-8" and let the SVG viewport slice the avatars.
 */
export const MEMBER_AXIS_PROPS = {
  type: 'category',
  dataKey: 'id',
  width: MEMBER_TICK_WIDTH,
  tickLine: false,
  axisLine: false,
  tickSize: 0,
  tickMargin: 0,
} as const;

const HOURS_CHART: ChartConfig = {
  hours: { label: 'Hours', color: 'var(--color-hygge)' },
};

/**
 * One spelling of an hours figure for the whole screen.
 *
 * `formatHours` is the Reports tab's, so a number here and the same number
 * there are written identically — and, more importantly, the donut's legend and
 * the list two inches below it cannot print 12.3h and 12.30h for one project.
 * `hoursOf` is the same value as a number, for the charts that need to plot it;
 * `hoursLabel` writes one of those numbers back out the same way.
 */
function hoursOf(seconds: number): number {
  return Number(formatHours(seconds));
}

function hoursText(seconds: number): string {
  return `${formatHours(seconds)}h`;
}

function hoursLabel(hours: number): string {
  return `${hours.toFixed(2)}h`;
}

export function DashboardTab() {
  const [period, setPeriod] = useState<PeriodPreset>('this-month');
  const [customFrom, setCustomFrom] = useState<Date | null>(() => new Date());
  const [customTo, setCustomTo] = useState<Date | null>(() => new Date());
  const [projectId, setProjectId] = useState<string>('all');

  // A preset's days come from the company calendar — the days a summarized
  // report is counted over — not from the reader's clock. [HW-929]
  const { data: periods, error: periodsError } = useTimePeriodPresets();
  const refreshPeriods = useRefreshTimePeriods();
  /**
   * The period asked for, then the period actually queried — they differ only
   * past a month, where the window is widened to whole ISO weeks so no bucket
   * on the line stands for a stray day or two. Everything on screen, headline
   * totals included, describes the queried window, and the dates are printed
   * above the cards so it is never a secret.
   */
  const range = useMemo(() => {
    const requested =
      period === 'custom' ? customRange(customFrom, customTo) : presetRange(period, periods);
    return requested ? summaryWindow(requested) : null;
  }, [period, customFrom, customTo, periods]);

  const { data, isLoading, error } = useTeamTimeSummary(
    range?.from,
    range?.to,
    projectId === 'all' ? undefined : projectId,
  );

  // The projects this reader's summary can contain, from the Time Tracker as
  // on the Reports tab: the summary reads every project for a holder of
  // `time.reports.all` or `time.edit.all`, which `/projects` would narrow to
  // their memberships. [HW-972]
  const { data: projectsData } = useReportProjects();
  const projects = projectsData?.data ?? [];
  const selectedProjectName = projects.find((p) => p.id === projectId)?.name;

  return (
    <div className="space-y-6">
      <Controls
        period={period}
        onPeriodChange={(next) => {
          setPeriod(next);
          // Asks the company calendar again, as Reports does. [HW-929]
          void refreshPeriods();
        }}
        customFrom={customFrom}
        customTo={customTo}
        onCustomFromChange={setCustomFrom}
        onCustomToChange={setCustomTo}
        projectId={projectId}
        onProjectChange={(next) => {
          setProjectId(next);
          void refreshPeriods();
        }}
        projects={projects}
      />

      {range && (
        <p className="text-sm text-muted-foreground">
          {formatDate(range.from, 'short')} — {formatDate(range.to, 'short')}
        </p>
      )}

      {!range && period !== 'custom' ? (
        periodsError ? (
          <EmptyCard>{extractErrorMessage(periodsError, 'Could not work out the period.')}</EmptyCard>
        ) : (
          <DashboardSkeleton />
        )
      ) : !range ? (
        <EmptyCard>Pick both ends of the period to see the team's time.</EmptyCard>
      ) : error ? (
        // The endpoint refuses a window it cannot serve — unparseable dates, an
        // inverted range, more than 400 days — and says which in the message.
        // Repeating its sentence beats inventing a vaguer one.
        <EmptyCard>{extractErrorMessage(error, 'Could not load the team summary.')}</EmptyCard>
      ) : isLoading || !data ? (
        <DashboardSkeleton />
      ) : data.loggedByCount === 0 ? (
        // The count is what is left AFTER the project filter, so an empty
        // result means one of two very different things. Saying "nobody logged
        // time" while the team logged plenty elsewhere would be a lie, and it
        // would strand the reader with no way back — the escape hatch lives in
        // the card that is no longer rendered.
        projectId === 'all' ? (
          <EmptyCard>
            <span>Nobody logged time in this period.</span>{' '}
            <Link to="/time" search={{ tab: 'team-time' }} className="font-medium text-hygge underline-offset-4 hover:underline">
              Open Team Time
            </Link>
          </EmptyCard>
        ) : (
          <EmptyCard>
            <p>
              No time logged on{' '}
              <span className="font-medium text-foreground">
                {selectedProjectName ?? 'this project'}
              </span>{' '}
              in this period.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-4"
              onClick={() => setProjectId('all')}
            >
              Show all projects
            </Button>
          </EmptyCard>
        )
      ) : (
        <>
          <Headline summary={data} />
          <div className="grid gap-4 xl:grid-cols-2">
            <ProjectsCard
              summary={data}
              selectedProjectId={projectId}
              onSelectProject={setProjectId}
            />
            <PeopleCard summary={data} />
          </div>
          <TrendCard summary={data} mode={range.mode} />
        </>
      )}
    </div>
  );
}

// ─── Controls ─────────────────────────────────────────────────────────────

interface ControlsProps {
  period: PeriodPreset;
  onPeriodChange: (period: PeriodPreset) => void;
  customFrom: Date | null;
  customTo: Date | null;
  onCustomFromChange: (date: Date | null) => void;
  onCustomToChange: (date: Date | null) => void;
  projectId: string;
  onProjectChange: (projectId: string) => void;
  projects: Array<{ id: string; name: string; color: string; status: string }>;
}

function Controls({
  period,
  onPeriodChange,
  customFrom,
  customTo,
  onCustomFromChange,
  onCustomToChange,
  projectId,
  onProjectChange,
  projects,
}: ControlsProps) {
  // Active projects, the rest behind the toggle, the selection always — a
  // click on the projects card can select an archived one. [HW-951]
  const [showArchived, setShowArchived] = useState(false);
  const listed = useMemo(
    () =>
      withArchivedHidden(projects, (p) => isInactiveProject(p.status), (p) => p.id, {
        showArchived,
        keep: [projectId],
      }),
    [projects, showArchived, projectId],
  );

  return (
    <div className="flex flex-wrap items-end gap-4">
      <div className="space-y-1.5">
        <Label className="text-xs font-medium text-muted-foreground">Period</Label>
        {/* The same presets, in the same order, as the Reports tab — the two
            screens describing the same month differently is the fastest way to
            lose a reader's trust in both. */}
        <Select value={period} onValueChange={(v) => onPeriodChange(v as PeriodPreset)}>
          <SelectTrigger className="h-9 w-[170px] text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="this-month">This Month</SelectItem>
            <SelectItem value="last-month">Last Month</SelectItem>
            <SelectItem value="this-week">This Week</SelectItem>
            <SelectItem value="last-week">Last Week</SelectItem>
            <SelectItem value="custom">Custom Range</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {period === 'custom' && (
        <>
          {/* Wide enough for the picker's widest date at either font size the input takes —
              see formatDate in ui/date-picker.tsx. */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-muted-foreground">From</Label>
            <DatePicker
              value={customFrom}
              onChange={onCustomFromChange}
              placeholder="Start date"
              maxDate={customTo ?? undefined}
              className="w-[220px]"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-muted-foreground">To</Label>
            <DatePicker
              value={customTo}
              onChange={onCustomToChange}
              placeholder="End date"
              minDate={customFrom ?? undefined}
              className="w-[220px]"
            />
          </div>
        </>
      )}

      <div className="space-y-1.5">
        <div className="flex items-center justify-between gap-3">
          <Label className="text-xs font-medium text-muted-foreground">Project</Label>
          <ShowArchivedToggle
            id="dashboard-show-archived"
            checked={showArchived}
            onCheckedChange={setShowArchived}
          />
        </div>
        <Select value={projectId} onValueChange={onProjectChange}>
          <SelectTrigger className="h-9 w-[200px] text-sm">
            <SelectValue placeholder="All projects" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All projects</SelectItem>
            {listed.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                <ProjectFilterLabel name={p.name} color={p.color} status={p.status} />
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

// ─── Headline ─────────────────────────────────────────────────────────────

function Headline({ summary }: { summary: TeamTimeSummary }) {
  const largest = summary.byProject[0];

  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <Metric label="Total" value={hoursText(summary.totalSeconds)} />
      {/* Averaged over the days somebody worked, not over the calendar: a month
          with three working days in it is not an eight-minute day. */}
      <Metric label="Daily average" value={hoursText(summary.dailyAvgSeconds)} note="per day with entries" />
      <Metric
        label="People logged"
        value={String(summary.loggedByCount)}
        note={summary.loggedByCount === 1 ? 'person' : 'people'}
      />
      <Metric
        label="Largest project"
        value={largest ? projectLabel(largest.projectName) : '—'}
        note={largest ? hoursText(largest.totalSeconds) : undefined}
      />
    </div>
  );
}

function Metric({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <Card>
      <CardContent className="px-4 py-3">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="mt-1 truncate text-xl font-semibold tabular-nums text-foreground" title={value}>
          {value}
        </p>
        {note && <p className="text-xs text-muted-foreground">{note}</p>}
      </CardContent>
    </Card>
  );
}

// ─── Card 1 — By project ──────────────────────────────────────────────────

function ProjectsCard({
  summary,
  selectedProjectId,
  onSelectProject,
}: {
  summary: TeamTimeSummary;
  selectedProjectId: string;
  onSelectProject: (projectId: string) => void;
}) {
  const rows = summary.byProject;
  const shares = useMemo(() => sharePercents(rows.map((r) => r.totalSeconds)), [rows]);
  const isFiltered = selectedProjectId !== 'all';

  /**
   * Slices carry the id the chart should select — except where the label is
   * ambiguous. Two projects sharing a name, or two deleted ones sharing the
   * same placeholder, are indistinguishable to a reader who clicks one, so they
   * arrive without an id and the donut renders them as plain text rather than
   * as a control that silently does nothing.
   */
  const slices = useMemo(() => {
    const seen = new Map<string, number>();
    for (const row of rows) {
      const label = projectLabel(row.projectName);
      seen.set(label, (seen.get(label) ?? 0) + 1);
    }
    return rows.map((row) => {
      const label = projectLabel(row.projectName);
      return {
        name: label,
        value: hoursOf(row.totalSeconds),
        id: seen.get(label) === 1 ? row.projectId : undefined,
        // The project's own colour, so the same project is the same hue here,
        // in the Reports table and on the board.
        color: row.projectColor || undefined,
      };
    });
  }, [rows]);

  const toggle = useCallback(
    (id: string) => onSelectProject(selectedProjectId === id ? 'all' : id),
    [onSelectProject, selectedProjectId],
  );

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">By project</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {isFiltered ? (
          <p className="text-sm text-muted-foreground">
            Showing{' '}
            <span className="font-medium text-foreground">
              {projectLabel(rows[0]?.projectName ?? '')}
            </span>{' '}
            only.{' '}
            <button
              type="button"
              onClick={() => onSelectProject('all')}
              className="cursor-pointer font-medium text-hygge underline-offset-4 hover:underline"
            >
              Show all projects
            </button>
          </p>
        ) : rows.length === 1 ? (
          // One sector is a circle, and a circle is not a chart.
          <p className="text-sm text-muted-foreground">
            All time went to{' '}
            <span className="font-medium text-foreground">{projectLabel(rows[0].projectName)}</span>.
          </p>
        ) : (
          <Donut
            slices={slices}
            unit="h"
            // The same two decimals the list below prints, so one project's
            // hours are not spelled two ways inside one card.
            format={(value) => value.toFixed(2)}
            centre={{ value: formatHours(summary.totalSeconds), label: 'hours total' }}
            onSelect={toggle}
          />
        )}

        {/* The full list, including whatever the ring folded into "Other". Every
            row carries its name, hours and share as text, so the chart above is
            a summary of this rather than the only place the numbers live. */}
        <ul className="space-y-0.5">
          {rows.map((row, i) => {
            const selected = row.projectId === selectedProjectId;
            return (
              <li key={row.projectId}>
                <button
                  type="button"
                  onClick={() => toggle(row.projectId)}
                  aria-pressed={selected}
                  className={cn(
                    'flex w-full cursor-pointer items-center gap-2 rounded-md px-1.5 py-1 text-left text-xs transition-colors hover:bg-muted/50',
                    selected && 'bg-muted/60',
                  )}
                >
                  <span
                    className="h-2 w-2 shrink-0 rounded-full"
                    style={{ backgroundColor: row.projectColor || 'var(--color-muted-foreground)' }}
                  />
                  <span className="min-w-0 flex-1 truncate text-muted-foreground">
                    {projectLabel(row.projectName)}
                  </span>
                  <span className="shrink-0 font-medium tabular-nums text-foreground">
                    {hoursText(row.totalSeconds)}
                  </span>
                  <span className="w-10 shrink-0 text-right tabular-nums text-muted-foreground">
                    {shares[i]}%
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}

// ─── Card 2 — By person ───────────────────────────────────────────────────

export interface MemberRow {
  id: string;
  name: string;
  hours: number;
  avatarUrl: string | null;
  isActive: boolean;
  totalSeconds: number;
}

function PeopleCard({ summary }: { summary: TeamTimeSummary }) {
  const formatName = useEmployeeNameFormatter();
  const [showAll, setShowAll] = useState(false);

  const rows: MemberRow[] = useMemo(
    () =>
      summary.byMember.map((m) => ({
        id: m.employeeId,
        // A person deleted between the aggregation and the name lookup arrives
        // with empty name fields. Their hours are real, so the bar stays — it
        // just says whose it was as best it can.
        name: memberLabel(
          formatName({ firstName: m.firstName, lastName: m.lastName, preferredName: m.preferredName }),
        ),
        hours: hoursOf(m.totalSeconds),
        avatarUrl: m.avatarUrl,
        isActive: m.isActive,
        totalSeconds: m.totalSeconds,
      })),
    [summary.byMember, formatName],
  );

  const { shown, restCount, restSeconds } = useMemo(
    () => topWithRest(rows, showAll ? rows.length : MEMBER_LIMIT),
    [rows, showAll],
  );

  // Built once rather than scanned per tick: recharts calls the tick renderer
  // for every category, and a linear search inside it is quadratic on a team
  // big enough to want "Show all".
  const rowById = useMemo(() => new Map(shown.map((row) => [row.id, row])), [shown]);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">By person</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <ChartContainer
          config={HOURS_CHART}
          className="aspect-auto w-full"
          style={{ height: shown.length * 34 + 16 }}
        >
          <BarChart
            data={shown}
            layout="vertical"
            margin={{ left: 0, right: 56, top: 4, bottom: 4 }}
          >
            <CartesianGrid horizontal={false} />
            <XAxis type="number" dataKey="hours" hide />
            <YAxis {...MEMBER_AXIS_PROPS} tick={<MemberTick rows={rowById} />} />
            <ChartTooltip
              cursor={false}
              content={
                <ChartTooltipContent
                  hideLabel
                  formatter={(value, _name, item) => (
                    <span className="flex w-full items-center justify-between gap-3">
                      {/* The bars are keyed by employee id, so the readout has
                          to go back to the row for a name a reader knows. */}
                      <span className="text-muted-foreground">
                        {(item as { payload?: MemberRow } | undefined)?.payload?.name ?? 'Hours'}
                      </span>
                      <span className="font-medium tabular-nums text-foreground">
                        {hoursLabel(Number(value))}
                      </span>
                    </span>
                  )}
                />
              }
            />
            <Bar dataKey="hours" fill="var(--color-hours)" radius={[0, 4, 4, 0]} barSize={14}>
              {/* Every bar carries its own number, so the chart is readable
                  without measuring it against the axis. */}
              <LabelList
                dataKey="hours"
                position="right"
                offset={8}
                fontSize={11}
                className="fill-foreground"
                formatter={(value: unknown) => hoursLabel(Number(value))}
              />
            </Bar>
          </BarChart>
        </ChartContainer>

        {restCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-full text-xs text-muted-foreground"
            onClick={() => setShowAll(true)}
          >
            Show all — {restCount} more {restCount === 1 ? 'person' : 'people'}, {hoursText(restSeconds)}
          </Button>
        )}
        {showAll && rows.length > MEMBER_LIMIT && (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-full text-xs text-muted-foreground"
            onClick={() => setShowAll(false)}
          >
            Show top {MEMBER_LIMIT}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * The avatar and name beside each bar, drawn as the category axis tick.
 *
 * Recharts clones this element per category, adding `x`, `y` and `payload`; the
 * `rows` prop passed at the call site survives the clone and is what turns an
 * employee id back into a face and a name.
 *
 * A tick is SVG, so the house `Avatar` — plain HTML, like every other face in
 * the app — needs a `foreignObject` to live in. That is the whole trick, and it
 * buys back what hand-drawn SVG could not do: photos that fill a circle instead
 * of being sliced by a clip path, and a name that ellipsises to the space it
 * actually has rather than to a character count guessed in advance.
 */
export function MemberTick({
  x = 0,
  y = 0,
  payload,
  rows,
}: {
  x?: number;
  y?: number;
  payload?: { value?: string };
  rows?: Map<string, MemberRow>;
}) {
  const row = payload?.value ? rows?.get(payload.value) : undefined;
  if (!row) return null;

  // Departed people are muted, but never by colour alone — the word travels
  // with the name. It sits outside the truncating span so an ellipsis eats the
  // end of a long name rather than the marker that qualifies it, and the title
  // carries both for anyone whose name did not fit.
  const full = row.isActive ? row.name : `${row.name} · former`;

  return (
    <foreignObject
      x={x - MEMBER_TICK_WIDTH}
      y={y - MEMBER_TICK_HEIGHT / 2}
      width={MEMBER_TICK_WIDTH}
      height={MEMBER_TICK_HEIGHT}
    >
      <div className="flex h-full w-full items-center gap-2 overflow-hidden pr-2" title={full}>
        <Avatar className="h-5 w-5 shrink-0 rounded-full">
          {/* Decorative: the name is right beside it, so a screen reader
              announcing the photo would only say it twice. */}
          <AvatarImage src={row.avatarUrl ?? undefined} alt="" />
          <AvatarFallback className="text-[8px]">{initialsOf(row.name)}</AvatarFallback>
        </Avatar>
        <span
          className={cn(
            'min-w-0 truncate text-[11px]',
            row.isActive ? 'text-foreground' : 'text-muted-foreground',
          )}
        >
          {row.name}
        </span>
        {!row.isActive && (
          <span className="shrink-0 text-[11px] text-muted-foreground">· former</span>
        )}
      </div>
    </foreignObject>
  );
}

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('');
}

// ─── Card 3 — Over time ───────────────────────────────────────────────────

function TrendCard({ summary, mode }: { summary: TeamTimeSummary; mode: 'day' | 'week' }) {
  const points = useMemo(() => {
    const series = mode === 'week' ? bucketByWeek(summary.byDay) : summary.byDay;
    return series.map((day) => ({
      date: day.date,
      hours: hoursOf(day.totalSeconds),
      weekend: mode === 'day' && isWeekendDayKey(day.date),
    }));
  }, [summary.byDay, mode]);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">
          Over time
          {/* Weekly buckets cover whole weeks, which is usually a little more
              than the period asked for. The dates say which. */}
          <span className="ml-2 font-normal text-muted-foreground">
            {mode === 'week'
              ? `by week · ${formatDate(summary.from, 'monthDay')} – ${formatDate(summary.to, 'monthDay')}`
              : 'by day'}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ChartContainer config={HOURS_CHART} className="aspect-auto h-[240px] w-full">
          <LineChart data={points} margin={{ left: 4, right: 12, top: 8, bottom: 4 }}>
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              minTickGap={24}
              tickFormatter={(value: string) => formatDate(value, 'monthDay')}
            />
            <YAxis
              width={40}
              tickLine={false}
              axisLine={false}
              tickFormatter={(value: number) => `${value}h`}
            />
            <ChartTooltip
              content={
                <ChartTooltipContent
                  labelFormatter={(_label, payload) => {
                    const date = String(payload?.[0]?.payload?.date ?? '');
                    return mode === 'week'
                      ? `Week of ${formatDate(date, 'short')}`
                      : formatDate(date, 'short');
                  }}
                  formatter={(value) => (
                    <span className="flex w-full items-center justify-between gap-3">
                      <span className="text-muted-foreground">Hours</span>
                      <span className="font-medium tabular-nums text-foreground">
                        {hoursLabel(Number(value))}
                      </span>
                    </span>
                  )}
                />
              }
            />
            <Line
              dataKey="hours"
              type="monotone"
              stroke="var(--color-hours)"
              strokeWidth={2}
              dot={<TrendDot />}
              activeDot={{ r: 4 }}
            />
          </LineChart>
        </ChartContainer>
        {points.some((p) => p.weekend) && (
          <p className="mt-1 text-[11px] text-muted-foreground">Hollow points are weekends.</p>
        )}
      </CardContent>
    </Card>
  );
}

/** Weekends read as hollow as well as muted — shape, not colour alone. */
function TrendDot({
  cx,
  cy,
  payload,
}: {
  cx?: number;
  cy?: number;
  payload?: { weekend?: boolean };
}) {
  if (cx == null || cy == null) return null;
  const weekend = payload?.weekend === true;
  return (
    <circle
      cx={cx}
      cy={cy}
      r={weekend ? 2.5 : 3}
      fill={weekend ? 'var(--color-background)' : 'var(--color-hours)'}
      stroke="var(--color-hours)"
      strokeWidth={weekend ? 1.5 : 0}
      opacity={weekend ? 0.7 : 1}
    />
  );
}

// ─── Empty and loading ────────────────────────────────────────────────────

function EmptyCard({ children }: { children: React.ReactNode }) {
  return (
    <Card>
      <CardContent className="px-6 py-14 text-center text-sm text-muted-foreground">
        {children}
      </CardContent>
    </Card>
  );
}

/**
 * Card-shaped skeletons rather than a centred spinner: the screen keeps its
 * layout while it loads, so nothing jumps when the numbers arrive.
 */
function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }, (_, i) => (
          <Card key={i}>
            <CardContent className="space-y-2 px-4 py-3">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-6 w-20" />
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="grid gap-4 xl:grid-cols-2">
        {Array.from({ length: 2 }, (_, i) => (
          <Card key={i}>
            <CardHeader className="pb-2">
              <Skeleton className="h-4 w-28" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-[220px] w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
      <Card>
        <CardHeader className="pb-2">
          <Skeleton className="h-4 w-28" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[240px] w-full" />
        </CardContent>
      </Card>
    </div>
  );
}
